import { createSlice, PayloadAction, createAsyncThunk } from '@reduxjs/toolkit';
import type { AuthState, UserData as UserDataFromTypes } from './types';
import { HttpClient } from "@/api/services/HttpClient";

// 🔥 NEW: Global logout state management
let isLoggingOut = false;

// 🔥 NEW: Set logout state
export const setLogoutState = (state: boolean) => {
  isLoggingOut = state;
};

// Enhanced user data interface to match your API response
interface UserData {
  id: string;
  fName: string;
  lName: string;
  name: string;
  email: string;
  status: string;
  role: string;
  defaultWorkspaceId: string;
  clientId: string;
  workspaceId?: string;
  userId?: string;
  permissions?: any[];
  // Add roleIds to match the actual API response
  roleIds?: {
    id: string;
    name: string;
    description: string;
  };
}

// Update the AuthState interface to include proper user data
interface EnhancedAuthState {
  isAuthenticated: boolean;
  loading: boolean;
  error: string | null;
  user: UserData | null;
  permissions: {
    can: any[];
    cannot: any[];
  };
  success: boolean;
}

// Define the initial state
const initialState: AuthState = {
  isAuthenticated: false,
  loading: false,
  isLoggingOut: false, // Simple logout loading state
  error: null,
  user: null,
  permissions: {
    can: [],
    cannot: [],
  },
  success: false
};

// FIXED: Create async thunk to fetch user profile and store in Redux
export const fetchUserProfile = createAsyncThunk(
  'auth/fetchUserProfile',
  async (workspaceId: string) => {
    try {
      // console.log('🔍 Fetching user profile for workspace:', workspaceId);

      const response = await HttpClient.apiClient.get("/profile", {
        // params: { workspace_id: workspaceId }
      });

      // console.log('🔍 Profile API response:', response.data);

      if (response.data?.status === "success" && response.data?.data) {
        const profileData = response.data.data;

        // console.log('🔍 Raw profile data from API:', profileData);

        // FIXED: Extract role from the correct nested structure
        let userRole = null;

        // console.log('🔍 Debugging role extraction:');
        // console.log('🔍 profileData.permissions:', profileData.permissions);
        // console.log('🔍 profileData.permissions length:', profileData.permissions?.length);
        // console.log('🔍 profileData.permissions[0]:', profileData.permissions?.[0]);
        // console.log('🔍 profileData.permissions[0].role:', profileData.permissions?.[0]?.role);

        // Method 1: Check permissions array
        if (profileData.permissions && profileData.permissions.length > 0) {
          userRole = profileData.permissions[0].role;
          // console.log('🔍 Role from permissions array:', userRole);
        }

        // Method 2: Check workspace.permission.role (fallback)
        if (!userRole && profileData.workspace?.permission?.role) {
          userRole = profileData.workspace.permission.role;
          // console.log('🔍 Role from workspace.permission:', userRole);
        }

        // Method 3: Check direct role field (fallback)
        if (!userRole && profileData.role) {
          userRole = profileData.role;
          // console.log('🔍 Role from direct field:', userRole);
        }

        // console.log('🔍 Final extracted role:', userRole);

        if (!userRole) {
          // console.error('❌ No role found in any location!');
          // console.log('Available data:', {
          //   permissions: profileData.permissions,
          //   workspace: profileData.workspace,
          //   directRole: profileData.role
          // });
        }

        // FIXED: Return structured user data that matches your API response
        const userData: Partial<UserDataFromTypes> = {
          id: profileData.id,
          fName: profileData.fName,
          lName: profileData.lName,
          name: profileData.name,
          email: profileData.email,
          status: profileData.status,
          role: userRole || 'UNKNOWN', // Add fallback to see if this helps
          defaultWorkspaceId: profileData.defaultWorkspaceId,
          clientId: profileData.clientId,
          workspaceId: workspaceId,
          userId: profileData.id,
          permissions: profileData.permissions || []
        };

        // console.log('✅ Processed user data for Redux:', userData);
        // console.log('🔍 Extracted role in userData:', userData.role);
        return userData;
      }

      throw new Error('Invalid profile response');
    } catch (error) {
      console.error("❌ Error fetching profile:", error);
      throw error;
    }
  }
);

// Create async thunk to initialize auth state on app start
export const initializeAuth = createAsyncThunk(
  'auth/initializeAuth',
  async (_, { dispatch }) => {
    try {
      // console.log('🚀 Initializing auth state...');

      // Check if user has token
      // const token = localStorage.getItem('token');
      // if (!token) {
      //   // console.log('❌ No token found, user not authenticated');
      //   return null;
      // }

      // Check if we have workspace ID
      const workspaceId = localStorage.getItem('workspaceId');
      if (!workspaceId) {
        // console.log('❌ No workspace ID found');
        return null;
      }

      // console.log('✅ Token and workspace ID found, fetching user profile...');

      // Fetch user profile
      const userProfile = await dispatch(fetchUserProfile(workspaceId)).unwrap();

      // console.log('✅ Auth initialization complete:', userProfile);
      return userProfile;
    } catch (error) {
      console.error('❌ Auth initialization failed:', error);
      // Clear invalid tokens
      localStorage.removeItem('token');
      localStorage.removeItem('workspaceId');
      throw error;
    }
  }
);

// Create the auth slice
const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setAuthLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setLogoutLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoggingOut = action.payload;
    },
    setAuthError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
      state.loading = false;
    },
    setAuthSuccess: (state, action: PayloadAction<boolean>) => {
      state.success = action.payload;
    },
    setUserData: (state, action: PayloadAction<UserDataFromTypes | null>) => {
      state.user = action.payload;
      state.isAuthenticated = !!action.payload;
      // console.log('🔄 User data set in Redux:', action.payload);
      // console.log('🔄 User role in Redux:', action.payload?.role);
    },
    // Enhanced logout to clear everything properly
    logout: (state) => {
      // 🔥 NEW: Set logout state to prevent permission loading
      setLogoutState(true);

      state.isAuthenticated = false;
      state.user = null;
      state.permissions = {
        can: [],
        cannot: [],
      };
      state.error = null;
      state.success = false;
      state.isLoggingOut = false; // Reset logout loading state

      // Clear localStorage items
      localStorage.removeItem("token");
      localStorage.removeItem("userId");
      localStorage.removeItem("userEmail");
      localStorage.removeItem("userName");
      localStorage.removeItem("userRole");
      localStorage.removeItem("workspaceId");
      localStorage.removeItem("clientId");

      // Clear cookies
      document.cookie = "customerToken=; Path=/; Expires=Thu, 01 Jan 1970 00:00:01 GMT;";
      document.cookie = "agent_email=; Path=/; Expires=Thu, 01 Jan 1970 00:00:01 GMT;";
      document.cookie = "workspaceId=; Path=/; Expires=Thu, 01 Jan 1970 00:00:01 GMT;";

      // console.log('🚪 User logged out, Redux state cleared');
    },

    // 🔥 NEW: Reset logout state (for when user signs back in)
    resetLogoutState: (state) => {
      setLogoutState(false);
    },

    clearError: (state) => {
      state.error = null;
    },
    setPermissions: (state, action: PayloadAction<any>) => {
      const payload = action.payload;

      if (payload && typeof payload === 'object' && payload.can && payload.cannot) {
        state.permissions = payload;
      } else if (Array.isArray(payload)) {
        state.permissions = {
          can: payload,
          cannot: []
        };
      } else {
        state.permissions = {
          can: [],
          cannot: []
        };
      }
      // console.log('🔐 Permissions set in Redux:', state.permissions);
    }
  },
  extraReducers: (builder) => {
    builder
      // Handle fetchUserProfile async thunk
      .addCase(fetchUserProfile.pending, (state) => {
        state.loading = true;
        state.error = null;
        // console.log('⏳ Fetching user profile...');
      })
      .addCase(fetchUserProfile.fulfilled, (state, action) => {
        state.loading = false;
        if (state.user) {
          state.user = { ...state.user, ...action.payload };
        } else {
          state.user = action.payload as UserDataFromTypes;
        }
        state.isAuthenticated = true;
        // console.log('✅ User profile loaded into Redux:', state.user);
        // if (action.payload.role) {
        //     console.log('✅ User role loaded:', action.payload.role);
        // }
      })
      .addCase(fetchUserProfile.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch user profile';
        console.error('❌ Failed to load user profile:', action.error);
      })
      // Handle initializeAuth async thunk
      .addCase(initializeAuth.pending, (state) => {
        state.loading = true;
        state.error = null;
        // console.log('⏳ Initializing auth...');
      })
      .addCase(initializeAuth.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload) {
          state.user = action.payload as UserDataFromTypes;
          state.isAuthenticated = true;
          // console.log('✅ Auth initialized with user data:', action.payload);
          // console.log('✅ Auth initialized with role:', action.payload.role);
        } else {
          state.isAuthenticated = false;
          state.user = null;
          // console.log('❌ Auth initialization failed - no user data');
        }
      })
      .addCase(initializeAuth.rejected, (state, action) => {
        state.loading = false;
        state.isAuthenticated = false;
        state.user = null;
        state.error = action.error.message || 'Auth initialization failed';
        console.error('❌ Auth initialization rejected:', action.error);
      });
  },
});

// Export the actions
export const {
  setAuthLoading,
  setLogoutLoading, // Simple logout loading action
  setAuthError,
  setAuthSuccess,
  setUserData,
  logout,
  clearError,
  setPermissions,
  resetLogoutState // 🔥 NEW
} = authSlice.actions;

// Export the reducer properly using both named export and default export
export const authReducer = authSlice.reducer;
export { authSlice };
export default authSlice.reducer;